// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

// Format date
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Truncate text
export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};


export const sortObjectsByDateDesc = (objArray,) => {
  return objArray.sort((a, b) => new Date(b["timestamp"]) - new Date(a["timestamp"]));
};


export const reversePatchSec = (symbol, sectype) => {
  const reversePatches = new Map([
    ["AUD", new Map([["symbol", "6A"]])],
    ["JPY", new Map([["symbol", "6J"]])],
    ["CAD", new Map([["symbol", "6C"]])],
    ["EUR", new Map([["symbol", "6E"]])],
    ["CHF", new Map([["symbol", "6S"]])],
    ["GBP", new Map([["symbol", "6B"]])],
    ["BRE", new Map([["symbol", "6L"]])],
    ["ZAR", new Map([["symbol", "6Z"]])],
    ["DX", new Map([["exchange", "ICEUS"]])],
    ["CT", new Map([["exchange", "ICEUS"]])],
    ["CC", new Map([["exchange", "ICEUS"]])],
    ["KC", new Map([["exchange", "ICEUS"]])],
    ["MNQ", new Map([["exchange", "CME_MINI"]])]
  ]);
  // Futures-only: DX, CT, CC, KC and SI are live stock tickers too.
  const isFutures = sectype === "CONTFUT" || sectype === "FUT";
  const patch = isFutures ? reversePatches.get(symbol) : undefined;
  let patchSymbol = undefined;
  let patchExchange = undefined;
  if (patch) {
    patchSymbol = patch.get("symbol");
    patchExchange = patch.get("exchange");
  }
  return [
    (patchSymbol === undefined) ?
      (symbol + (sectype === "CONTFUT" ? "1!" : "")) :
      (patchSymbol + (sectype === "CONTFUT" ? "1!" : "")),
    patchExchange
  ];
};


export const shortenSourceName = (sourceName) => {
  const patches = new Map([
    ["cross", "X"],
    ["dot", "O"],
    ["red", "R"],
    ["yellow", "Y"],
    ["green", "G"]
  ]);
  return patches.get(sourceName);
};

export const reversePatchCrypto = (symbol) => {
  return symbol.replace("/USDT:USDT", "USDT.P");
}

// Keep in sync with db_client config.py TIMEFRAMES_GROUPS (scanner group timeframes).
export const timeframes = [
  'ZERO',
  '15s', '30s', '45s',
  '1m', '2m', '3m', '4m', '6m', '8m', '12m', '16m', '23m', '32m', '45m', '64m', '90m',
  '2h', '3h', '4h', '6h', '8h', '12h', '16h', '1d', '2d', '3d', '4d', '8d', '12d', '16d'
]

export const timeframesTos = [
  'A', 'B', 'C'
]

export const sourcesVer2 = [
  'x1', 'x2', 'x3', 'p1', 'p2', 'p3',
  'c1', 'c2', 'c3', 'o1', 'o2', 'o3',
  'r1', 'r2', 'r3', 'y1', 'y2', 'y3',
  'g1', 'g2', 'g3'
]

export const sourcesTos = [
  't', 'm', 's'
]

export const services = [
  { name: "Tornado", url: "/health/healthcheck-data-vis" },
  { name: "OKX Strategy", url: "/health/healthcheck-okx-strategy" },
  { name: "IB Strategy", url: "/health/healthcheck-ib-strategy" },
  { name: "IB Trader", url: "/health/healthcheck-ib-client" },
  { name: "OKX Trader", url: "/health/healthcheck-okx-client" },
];


// Instrument type filter for the asset list. Futures covers both CONTFUT and FUT.
export const instrumentTypeFilters = ['futures', 'stocks', 'all'];

export const matchesInstrumentType = (sectype, filter) => {
  if (filter === 'all') return true;
  const type = (sectype || '').toUpperCase();
  return filter === 'stocks' ? type === 'STK' : (type === 'CONTFUT' || type === 'FUT');
};

// Only IB carries a sectype; crypto assets are never filtered out.
export const assetMatchesInstrumentType = (asset, filter) => (
  asset?.broker !== 'ib' || matchesInstrumentType(asset.sectype, filter)
);

// Badge for anything that is not a continuous future; CONTFUT is the default and
// stays unlabelled.
export const instrumentTypeBadge = (sectype) => {
  const type = (sectype || '').toUpperCase();
  if (!type || type === 'CONTFUT') return null;
  return type === 'STK' ? 'Stock' : type;
};

/**
 * One row per instrument for a COUNTER folder.
 *
 * Each source account returns its whole book, so an instrument carried by two of
 * them arrives twice; in a folder it is one instrument. The row on the account
 * the library named wins, whatever order the fetches land in.
 */
export const assetKey = (a) => `${a.broker}|${a.symbol}|${a.sectype || ''}`;

/**
 * The open COUNTER folder as a fresh library read has it: null when the folder
 * is gone, the same object when nothing in it changed (so nothing reloads).
 */
export const refreshOpenFolder = (open, library) => {
  const folder = library.folders.find((f) => f.id === open.id);
  if (!folder) return null;
  const instruments = library.instruments.filter((i) => i.folder_id === folder.id);
  const shape = (list) => list.map((i) => `${assetKey(i)}|${i.account}`).sort().join(',');
  if (folder.name === open.name && shape(instruments) === shape(open.instruments)) return open;
  return { ...folder, instruments };
};

// The folder's instruments whose parent stopped carrying them, fresh off a library read.
export const folderStaleParents = (library, folderId) => library.instruments.filter(
  (i) => i.folder_id === folderId && i.needs_parent,
);

// The folder instrument a notification is about; null when it left the folder.
// ponytail: notifications carry no sectype, so same symbol in two sectypes picks the first.
export const folderAssetOf = (assets, n) => assets.find((a) => a.broker === n.broker && a.symbol === n.symbol) || null;

export const pickFolderAssets =(instruments, fetched) => {
  const wanted = new Map(instruments.map((i) => [assetKey(i), i.account]));
  const picked = new Map();
  fetched.forEach((a) => {
    if (!a || !wanted.has(assetKey(a))) return;
    const key = assetKey(a);
    if (!picked.has(key) || a.account === wanted.get(key)) picked.set(key, a);
  });
  return [...picked.values()];
};
